"use client";

import { c as _c } from "react/compiler-runtime";
import { getDefaultHeroHeaderView, HERO_HEADER_VIEW, HERO_HEADER_VIEW_COOKIE_NAME, usePathname, usePersona } from "@opensea/navigation";
import { getBreakpointPixelValue, useIsGreaterThanOrEqualToLg } from "@opensea/ui-kit";
import { useCookie, useEventListener, useMountEffect, useOnChange } from "@opensea/ui-kit/hooks";
import { createContext, useContext, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useMinHeight } from "@/hooks/use-min-height";
import { LONG_LASTING_COOKIE_EXPIRATION } from "@/lib/cookie";
import { useTopNavbarStore } from "@/stores/use-top-navbar-store";
import { topNavbarVariantsForHeroHeaderView } from "./utils";
const DEFAULT_SCROLL_BREAKPOINT = 273;
const HeroHeaderViewContext = createContext({
  available: false,
  headerView: HERO_HEADER_VIEW.full,
  setHeaderView: () => undefined,
  headerViewScrollLocked: false,
  fallbackImageUrl: undefined,
  setFallbackImageUrl: () => undefined,
  scrollBreakpoint: DEFAULT_SCROLL_BREAKPOINT,
  scrollToTop: () => false
});
export function HeroHeaderViewProvider(t0) {
  const $ = _c(43);
  const {
    children,
    headerView: initialHeaderView,
    headerRef,
    forceInitialView: t1
  } = t0;
  const forceInitialView = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const {
    persona
  } = usePersona();
  const {
    setVariants: setTopNavbarVariants
  } = useTopNavbarStore(useShallow(_temp));
  const isGreaterThanOrEqualToLg = useIsGreaterThanOrEqualToLg();
  const [headerViewCookie, setHeaderViewCookie] = useCookie(HERO_HEADER_VIEW_COOKIE_NAME);
  const [headerViewOverride, setHeaderViewOverride] = useState(null);
  const mobileHeaderView = isGreaterThanOrEqualToLg ? null : "full";
  const headerView = mobileHeaderView ?? headerViewOverride ?? (forceInitialView ? null : headerViewCookie) ?? initialHeaderView;
  const [fallbackImageUrl, setFallbackImageUrl] = useState(null);
  const [headerViewLocked, setHeaderViewLocked] = useState(headerView === "compact");
  const [scrollBreakpoint, setScrollBreakpoint] = useState(DEFAULT_SCROLL_BREAKPOINT);
  let t2;
  if ($[0] !== headerRef) {
    t2 = function updateScrollBreakpoint() {
      if (!headerRef?.current) {
        return;
      }
      const proposedScrollBreakpoint = headerRef.current.clientHeight - 64 - 70;
      if (proposedScrollBreakpoint <= 0) {
        return;
      }
      setScrollBreakpoint(proposedScrollBreakpoint);
    };
    $[0] = headerRef;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updateScrollBreakpoint = t2;
  useMountEffect(updateScrollBreakpoint);
  useEventListener("resize", updateScrollBreakpoint);
  useMinHeight(headerViewLocked ? null : `calc(100vh + ${scrollBreakpoint.toString()}px)`);
  let t3;
  if ($[2] !== headerViewLocked || $[3] !== scrollBreakpoint || $[4] !== setHeaderViewCookie || $[5] !== setTopNavbarVariants) {
    t3 = function setHeaderView(next, t4) {
      const trigger = t4 === undefined ? "manual" : t4;
      if (trigger === "scroll" && headerViewLocked) {
        return;
      }
      setHeaderViewLocked(trigger === "manual" && next === "compact");
      setTopNavbarVariants(topNavbarVariantsForHeroHeaderView(next));
      if (trigger === "scroll") {
        setHeaderViewOverride(next);
      }
      if (trigger === "manual") {
        setHeaderViewOverride(null);
        setHeaderViewCookie(next, {
          expires: LONG_LASTING_COOKIE_EXPIRATION
        });
        if (next === "full") {
          if (window.scrollY > scrollBreakpoint) {
            window.scrollTo(0, scrollBreakpoint);
          }
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      }
    };
    $[2] = headerViewLocked;
    $[3] = scrollBreakpoint;
    $[4] = setHeaderViewCookie;
    $[5] = setTopNavbarVariants;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const setHeaderView = t3;
  let t4;
  if ($[7] !== headerView || $[8] !== scrollBreakpoint || $[9] !== setHeaderView) {
    t4 = function handleScroll() {
      if (window.innerWidth < getBreakpointPixelValue("lg")) {
        return;
      }
      if (window.scrollY >= scrollBreakpoint && headerView === "full") {
        setHeaderView("compact", "scroll");
      }
      if (window.scrollY < scrollBreakpoint && headerView === "compact") {
        setHeaderView("full", "scroll");
      }
    };
    $[7] = headerView;
    $[8] = scrollBreakpoint;
    $[9] = setHeaderView;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleScroll = t4;
  let t5;
  if ($[11] !== handleScroll) {
    t5 = () => {
      handleScroll();
    };
    $[11] = handleScroll;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEventListener("scroll", t5);
  let t6;
  if ($[13] !== persona || $[14] !== setHeaderView) {
    t6 = () => {
      setHeaderView(getDefaultHeroHeaderView(persona));
    };
    $[13] = persona;
    $[14] = setHeaderView;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useOnChange(persona, t6);
  let t7;
  if ($[16] !== headerView || $[17] !== headerViewLocked || $[18] !== scrollBreakpoint) {
    t7 = function scrollToTop() {
      bb85: switch (headerView) {
        case "compact":
          {
            window.scrollTo(0, headerViewLocked ? 0 : scrollBreakpoint);
            break bb85;
          }
        default:
          {}
      }
    };
    $[16] = headerView;
    $[17] = headerViewLocked;
    $[18] = scrollBreakpoint;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const scrollToTop = t7;
  let t8;
  if ($[20] !== scrollToTop) {
    t8 = () => {
      scrollToTop();
    };
    $[20] = scrollToTop;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useOnChange(pathname, t8, _temp2);
  let t9;
  if ($[22] !== headerView) {
    t9 = topNavbarVariantsForHeroHeaderView(headerView);
    $[22] = headerView;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const topNavbarVariants = t9;
  let t10;
  if ($[24] !== handleScroll || $[25] !== setTopNavbarVariants || $[26] !== topNavbarVariants) {
    t10 = () => {
      handleScroll();
      setTopNavbarVariants(topNavbarVariants);
    };
    $[24] = handleScroll;
    $[25] = setTopNavbarVariants;
    $[26] = topNavbarVariants;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  useMountEffect(t10);
  let t11;
  if ($[28] !== setTopNavbarVariants || $[29] !== topNavbarVariants) {
    t11 = () => {
      setTopNavbarVariants(topNavbarVariants);
    };
    $[28] = setTopNavbarVariants;
    $[29] = topNavbarVariants;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== setTopNavbarVariants) {
    t12 = () => {
      setTopNavbarVariants(undefined);
    };
    $[31] = setTopNavbarVariants;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  useOnChange(topNavbarVariants, t11, t12);
  let t13;
  if ($[33] !== fallbackImageUrl || $[34] !== headerView || $[35] !== headerViewLocked || $[36] !== scrollBreakpoint || $[37] !== scrollToTop || $[38] !== setHeaderView) {
    t13 = {
      available: true,
      headerView,
      setHeaderView,
      headerViewScrollLocked: headerViewLocked,
      fallbackImageUrl,
      setFallbackImageUrl,
      scrollBreakpoint,
      scrollToTop
    };
    $[33] = fallbackImageUrl;
    $[34] = headerView;
    $[35] = headerViewLocked;
    $[36] = scrollBreakpoint;
    $[37] = scrollToTop;
    $[38] = setHeaderView;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const value = t13;
  let t14;
  if ($[40] !== children || $[41] !== value) {
    t14 = <HeroHeaderViewContext.Provider value={value}>{children}</HeroHeaderViewContext.Provider>;
    $[40] = children;
    $[41] = value;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}
function _temp2() {
  window.scrollTo(0, 0);
}
function _temp(state) {
  return {
    setVariants: state.setVariants
  };
}
export function HeroHeaderViewProviderSkeleton(t0) {
  const $ = _c(5);
  const {
    headerView,
    children
  } = t0;
  let t1;
  if ($[0] !== headerView) {
    t1 = {
      available: true,
      headerView,
      setHeaderView: _temp3,
      headerViewScrollLocked: false,
      fallbackImageUrl: undefined,
      setFallbackImageUrl: _temp4,
      scrollBreakpoint: DEFAULT_SCROLL_BREAKPOINT,
      scrollToTop: _temp5
    };
    $[0] = headerView;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  let t2;
  if ($[2] !== children || $[3] !== value) {
    t2 = <HeroHeaderViewContext.Provider value={value}>{children}</HeroHeaderViewContext.Provider>;
    $[2] = children;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp5() {
  return false;
}
function _temp4() {
  return false;
}
function _temp3() {
  return false;
}
export const useHeroHeaderView = () => {
  return useContext(HeroHeaderViewContext);
};